import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, take, throwError } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { SessionEventsService } from '../auth/session-events.service';
import { NotificationService } from '../notifications/notification.service';

/**
 * Auth endpoints we must never refresh-against (would loop infinitely).
 * These represent the auth lifecycle itself: a 401 on them means something
 * other than "expired access token" — it means the credentials are wrong
 * or the refresh token has itself expired / been blacklisted.
 */
const AUTH_PATHS = [
  '/auth/token/',
  '/auth/token/refresh/',
  '/auth/token/verify/',
  '/auth/token/logout/',
] as const;

function isAuthEndpoint(url: string): boolean {
  return AUTH_PATHS.some((path) => url.endsWith(path));
}

/**
 * Refresh interceptor (Phase H Stage 1 / ADR-0045 + Stage 2 / ADR-0046).
 *
 * On 401 from any non-auth endpoint:
 *   1. Ask AuthService.refresh() for a fresh access token. Concurrent 401s
 *      share a single in-flight refresh promise (AuthService.refreshing).
 *   2. Retry the original request with the new Bearer token.
 *   3. Toast "Session refreshed" at top-inline-end on success.
 *   4. If refresh fails, fire SessionEventsService.expired() and pause —
 *      the application shell opens the session-expired modal in response.
 *      When the user re-authenticates, resumed$ emits the new token and
 *      the original request is retried. When the user cancels, resumed$
 *      emits null and the original 401 is propagated to the subscriber.
 *
 * Innermost interceptor (closest to the backend) so it sees the raw 401
 * before any other interceptor reacts to it.
 */
export const refreshInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const authService = inject(AuthService);
  const notifications = inject(NotificationService);
  const sessionEvents = inject(SessionEventsService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }
      // 401 on /auth/* endpoints is the terminal case — don't try to refresh
      // (would loop) or transform (login UIs need to see the 401 as-is).
      if (isAuthEndpoint(req.url)) {
        return throwError(() => err);
      }

      // Student-portal endpoints use the STUDENT token stack (ADR-0036)
      // which has no UI refresh mechanism — refreshing the STAFF token
      // here would be the wrong stack entirely, and the session-expired
      // modal belongs to the staff shell. Let the student pages handle
      // their own 401s (StudentAuthService.refreshProfile clears state
      // and the studentAuthGuard redirects on next navigation).
      if (req.url.includes('/student-portal/')) {
        return throwError(() => err);
      }

      return from(authService.refresh()).pipe(
        switchMap((newAccessToken) => {
          notifications.sessionRefreshed();
          const retried = req.clone({
            setHeaders: { Authorization: `Bearer ${newAccessToken}` },
          });
          return next(retried);
        }),
        catchError(() => {
          // Refresh path failed → ask the shell to open the session-expired
          // modal, then wait for the user's re-authentication or cancel.
          sessionEvents.expired();
          return sessionEvents.resumed$.pipe(
            take(1),
            switchMap((newAccess) => {
              if (!newAccess) {
                // User cancelled re-login. Propagate the original 401 so
                // the shell's logout-and-redirect flow takes over.
                return throwError(() => err);
              }
              const retried = req.clone({
                setHeaders: { Authorization: `Bearer ${newAccess}` },
              });
              return next(retried);
            }),
          );
        }),
      );
    }),
  );
};
